import React, { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";
import { Company } from "$types";
import { useDebounce } from "$utils/customHooks";
import LimitTag from "$components/LimitTag";

interface ProviderSelectProps {
  value?: string | string[];
  onChange: (
    providerId: string | string[] | null,
    provider: Company | Company[] | null
  ) => void;
  onBlur?: (e: any) => void;
  companyId?: string;
  disabled?: boolean;
  error?: boolean;
  rowsPerPage?: number;
  className?: string;
  placeholder?: string;
  minCharsToTriggerSearch?: number;
  label?: string;
  multiple?: boolean;
  limitTags?: number;
}

const ProviderSelect: React.FC<ProviderSelectProps> = ({
  value,
  onChange,
  onBlur,
  companyId,
  disabled = false,
  error = false,
  rowsPerPage = 20,
  className,
  placeholder = "Sélectionnez un fournisseur",
  minCharsToTriggerSearch = 0,
  label,
  multiple = false,
  limitTags = 2,
}) => {
  const [options, setOptions] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<Company[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const debouncedInputValue = useDebounce(inputValue, 300);

  // Fetch providers based on search input
  useEffect(() => {
    const fetchProviders = async () => {
      if (!companyId) {
        setOptions([]);
        return;
      }

      // Always show selected providers if we have them and user isn't actively typing
      let baseOptions: Company[] = [];
      if (selectedProviders.length > 0 && !isUserTyping) {
        baseOptions = [...selectedProviders];
      }

      // If search input is below minimum and we have selected providers, just show those
      if (
        debouncedInputValue.length < minCharsToTriggerSearch &&
        minCharsToTriggerSearch > 0
      ) {
        setOptions(baseOptions);
        return;
      }

      setLoading(true);

      try {
        const filters = {
          isActive: true,
          $inCompanyTypes: ["supplier"],
          supplierOf: companyId,
          ...(debouncedInputValue && { name: debouncedInputValue }),
          rowsPerPage,
          page: 1,
        };

        const cacheKey = [CacheInvalidationAction.GET_COMPANIES, filters];
        let providersResponse = (await queryClient.getQueryData(cacheKey)) as
          | { data: Company[] }
          | undefined;

        if (!providersResponse) {
          providersResponse = (await queryClient.fetchQuery(
            cacheKey,
            () => CompanyServices.getCompanies(filters),
            { cacheTime: 30 * 1000 }
          )) as { data: Company[] };
        }

        const providers = providersResponse?.data || [];

        // Merge search results with selected providers to avoid duplicates
        const uniqueProviders = [...providers];
        selectedProviders.forEach((selectedProvider) => {
          if (!providers.find((p) => p._id === selectedProvider._id)) {
            uniqueProviders.unshift(selectedProvider);
          }
        });

        setOptions(uniqueProviders);
      } catch (error) {
        setOptions(baseOptions);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [
    companyId,
    debouncedInputValue,
    minCharsToTriggerSearch,
    rowsPerPage,
    selectedProviders,
    isUserTyping,
  ]);

  // Initialize selected providers when value changes
  useEffect(() => {
    const fetchSelectedProviders = async () => {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setSelectedProviders([]);
        if (!isUserTyping) {
          setInputValue("");
        }
        return;
      }

      const valueArray = Array.isArray(value) ? value : [value];

      // Filter out invalid MongoDB ObjectIds
      const validIds = valueArray.filter(
        (id) =>
          typeof id === "string" &&
          id.length === 24 &&
          /^[0-9a-fA-F]{24}$/.test(id)
      );

      if (validIds.length === 0) {
        return;
      }

      // Check if we already have all these providers in our options
      const existingProviders = options.filter((provider) =>
        validIds.includes(provider._id)
      );

      if (existingProviders.length === validIds.length) {
        setSelectedProviders(existingProviders);
        if (!isUserTyping && !multiple && existingProviders[0]) {
          setInputValue(existingProviders[0].name);
        }
        return;
      }

      // Only fetch missing providers
      const existingIds = existingProviders.map((p) => p._id);
      const missingIds = validIds.filter((id) => !existingIds.includes(id));

      if (missingIds.length === 0) {
        return;
      }

      try {
        const filters = {
          $inId: missingIds,
          rowsPerPage: missingIds.length,
          page: 1,
        };

        const result = (await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          () => CompanyServices.getCompanies(filters),
          { cacheTime: 60 * 1000 }
        )) as { data: Company[] };

        const fetchedProviders = result?.data || [];
        const allProviders = [...existingProviders, ...fetchedProviders];

        setSelectedProviders(allProviders);

        if (!isUserTyping && !multiple && allProviders[0]) {
          setInputValue(allProviders[0].name);
        }
      } catch (error) {
        console.error("Error fetching selected providers:", error);
      }
    };

    fetchSelectedProviders();
  }, [value, options, multiple, isUserTyping]);

  const handleChange = (event: any, newValue: Company | Company[] | null) => {
    if (multiple) {
      const providers = Array.isArray(newValue) ? newValue : [];
      setSelectedProviders(providers);
      setInputValue(""); // Clear input for multiple select
      onChange(
        providers.map((p) => p._id),
        providers
      );
    } else {
      const provider = Array.isArray(newValue) ? newValue[0] : newValue;
      setSelectedProviders(provider ? [provider] : []);

      if (provider) {
        setInputValue(provider.name);
        onChange(provider._id, provider);
      } else {
        setInputValue("");
        onChange(null, null);
      }
    }

    setIsUserTyping(false);
  };

  const handleInputChange = (
    event: any,
    newInputValue: string,
    reason: string
  ) => {
    if (reason === "input") {
      setIsUserTyping(true);
      setInputValue(newInputValue);
    } else if (reason === "reset") {
      setIsUserTyping(false);
      if (multiple) {
        // For multiple select, keep input empty after selection
        setInputValue("");
      }
    }
  };

  const handleBlur = (event: any) => {
    setIsUserTyping(false);

    // Reset input based on selection
    if (multiple) {
      setInputValue(""); // Keep empty for multiple
    } else if (selectedProviders[0]) {
      setInputValue(selectedProviders[0].name);
    }

    if (onBlur) {
      onBlur(event);
    }
  };

  const getOptionLabel = (option: Company) => {
    return option.name;
  };

  const noOptionsText = useMemo(() => {
    if (!companyId) {
      return "Sélectionnez d'abord une entreprise";
    }
    if (loading) {
      return "Chargement...";
    }
    if (
      minCharsToTriggerSearch > 0 &&
      inputValue.length < minCharsToTriggerSearch &&
      selectedProviders.length === 0
    ) {
      return `Entrer au moins ${minCharsToTriggerSearch} caractère${
        minCharsToTriggerSearch > 1 ? "s" : ""
      }`;
    }
    if (options.length === 0) {
      return debouncedInputValue
        ? "Aucun fournisseur ne correspond à votre recherche"
        : "Aucun fournisseur trouvé pour cette entreprise";
    }
    return "Aucun fournisseur ne correspond";
  }, [
    companyId,
    loading,
    options.length,
    debouncedInputValue,
    minCharsToTriggerSearch,
    inputValue.length,
    selectedProviders.length,
  ]);

  const currentValue = multiple
    ? selectedProviders
    : selectedProviders[0] || null;

  return (
    <div style={{ flex: 1 }}>
      <Autocomplete
        multiple={multiple}
        value={currentValue}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        options={Array.isArray(options) ? options : []}
        getOptionLabel={getOptionLabel}
        loading={loading}
        disabled={disabled || !companyId}
        noOptionsText={noOptionsText}
        className={className}
        disableListWrap
        filterOptions={(x) => x}
        limitTags={limitTags}
        getLimitTagsText={(more) => <LimitTag nbMore={more} />}
        isOptionEqualToValue={(option, value) => {
          if (
            option &&
            value &&
            typeof option === "object" &&
            typeof value === "object" &&
            !Array.isArray(option) &&
            !Array.isArray(value)
          ) {
            return (option as Company)._id === (value as Company)._id;
          }
          return false;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            placeholder={
              !companyId
                ? "Sélectionnez d'abord une entreprise"
                : multiple && selectedProviders.length > 0
                ? undefined
                : placeholder
            }
            error={error}
          />
        )}
      />
    </div>
  );
};

export default ProviderSelect;
